import React, { useMemo, useState } from 'react';
import { Check, Plus, Repeat } from 'lucide-react';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { SocialAvatar } from '@/components/social/SocialAvatar';
import { useBusinessAccounts, type BusinessAccount } from '@/hooks/useBusinessAccounts';
import { useBusinessAccountContext } from '@/hooks/useBusinessAccountContext';
import { useBusinessProfile } from '@/hooks/useBusinessProfile';
import { useOrganizationEntitlements } from '@/hooks/useFeatureLimits';
import { cn } from '@/lib/utils';
import { ACCOUNT_BUSINESS_ACCOUNTS_URL } from './SidebarAccountFooter';

/** New Account Settings shell. Adding a workspace still gates on the ADMIN `/home/business-accounts` path. */
export const BUSINESS_ACCOUNTS_URL = ACCOUNT_BUSINESS_ACCOUNTS_URL;
const BUSINESS_ACCOUNTS_GATE_URL = '/home/business-accounts';

function accountInitial(name: string | undefined): string {
  const letter = (name || '').trim().charAt(0);
  return letter ? letter.toUpperCase() : 'A';
}

function accountPhone(phone: string | undefined | null): string {
  const trimmed = (phone || '').trim();
  return trimmed || 'No connected number';
}

function InitialGlyph({
  name,
  className,
}: {
  name: string | undefined;
  className: string;
}) {
  return (
    <div className={className}>
      {accountInitial(name)}
    </div>
  );
}

/**
 * WhatsApp Business profile picture — same live `useBusinessProfile` fetch the
 * inbox Channels header uses. The URL lives on pps.whatsapp.net and expires in
 * ~3 days, so it is never persisted; SocialAvatar degrades to the letter glyph
 * when Meta omits the picture or the URL 403s mid-session.
 */
function WorkspaceAvatar({
  accountId,
  name,
  sizeClass,
  loading = 'lazy',
}: {
  accountId?: string | null;
  name?: string;
  sizeClass: string;
  loading?: 'lazy' | 'eager';
}) {
  const { data: profile } = useBusinessProfile(accountId || undefined, {
    enabled: Boolean(accountId),
  });
  const glyphClass = cn(
    'flex shrink-0 items-center justify-center rounded-full bg-[#1D2939] text-[11px] font-semibold text-white',
    sizeClass,
  );
  return (
    <SocialAvatar
      src={profile?.profile_picture_url}
      alt={name || 'Workspace'}
      className={cn('shrink-0 rounded-full object-cover', sizeClass)}
      fallback={<InitialGlyph name={name} className={glyphClass} />}
      loading={loading}
    />
  );
}

interface SidebarWorkspaceSwitcherProps {
  collapsed: boolean;
  canOpen: (url: string, featureKey: string | null) => boolean;
  onNavigate: (url: string) => void;
}

function WorkspaceIdentity({
  name,
  phone,
}: {
  name: string;
  phone?: string | null;
}) {
  return (
    <div className="min-w-0 flex-1 text-left">
      <div className="truncate text-[14px] font-medium leading-5 text-[#101828]">{name}</div>
      <div className="truncate text-[12px] leading-4 text-[#667085]">{accountPhone(phone)}</div>
    </div>
  );
}

function WorkspaceRowBody({ account, active }: {
  account: BusinessAccount;
  active: boolean;
}) {
  return (
    <>
      <WorkspaceAvatar
        accountId={account.id}
        name={account.name}
        sizeClass="h-8 w-8"
      />
      <WorkspaceIdentity name={account.name} phone={account.phone_number} />
      {active ? <Check className="h-4 w-4 shrink-0 text-[#0EB64F]" /> : null}
    </>
  );
}

function rowClass(active: boolean) {
  return cn(
    'flex w-full items-center gap-2.5 rounded-xl px-2 py-2 text-left',
    active ? 'bg-[#E7F7EC]' : 'hover:bg-[#F9FAFB]',
  );
}

function WorkspaceMenu({
  accounts,
  selectedAccountId,
  isPending,
  showEmpty,
  canAddWorkspace,
  onSelect,
  onAdd,
}: {
  accounts: BusinessAccount[];
  selectedAccountId: string | null;
  isPending: boolean;
  showEmpty: boolean;
  canAddWorkspace: boolean;
  onSelect: (id: string) => void;
  onAdd: () => void;
}) {
  return (
    <>
      <div className="px-1.5 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-[#98A2B3]">
        Workspaces
      </div>
      <div className="max-h-[220px] overflow-y-auto">
        {isPending ? (
          <div className="px-1.5 py-2 text-[13px] text-[#667085]">Loading workspaces…</div>
        ) : showEmpty ? (
          <div className="px-1.5 py-2 text-[13px] text-[#667085]">No business accounts connected</div>
        ) : (
          accounts.map((account) => {
            const active = account.id === selectedAccountId;
            return (
              <button
                key={account.id}
                type="button"
                onClick={() => onSelect(account.id)}
                className={rowClass(active)}
              >
                <WorkspaceRowBody
                  account={account}
                  active={active}
                />
              </button>
            );
          })
        )}
      </div>
      {canAddWorkspace ? (
        <button
          type="button"
          data-testid="sidebar-add-workspace"
          onClick={onAdd}
          className="mt-1 flex h-9 w-full items-center gap-2 rounded-lg px-2 font-medium text-[#0EB64F] hover:bg-[#F9FAFB]"
        >
          <Plus className="h-4 w-4" />
          Add workspace
        </button>
      ) : null}
    </>
  );
}

export function SidebarWorkspaceSwitcher({
  collapsed,
  canOpen,
  onNavigate,
}: SidebarWorkspaceSwitcherProps) {
  const { data: accounts, isPending } = useBusinessAccounts();
  const { data: entitlements } = useOrganizationEntitlements();
  const { selectedAccountId, setSelectedAccountId } = useBusinessAccountContext();
  const [open, setOpen] = useState(false);

  const list = accounts ?? [];
  const showEmpty = !isPending && list.length === 0;

  const selectedAccount = useMemo(() => {
    if (!list.length) return null;
    return list.find((a) => a.id === selectedAccountId) || null;
  }, [list, selectedAccountId]);

  const canAddWorkspace = useMemo(() => {
    if (!canOpen(BUSINESS_ACCOUNTS_GATE_URL, 'business_accounts')) return false;
    if (!entitlements?.entitlements?.limits) return false;

    const limit = entitlements.entitlements.limits.business_accounts;
    if (limit === -1) return true;

    return list.length < (limit || 0);
  }, [canOpen, entitlements, list.length]);

  const closeAndSelect = (id: string) => {
    setSelectedAccountId(id);
    setOpen(false);
  };

  const closeAndAdd = () => {
    setOpen(false);
    onNavigate(BUSINESS_ACCOUNTS_URL);
  };

  const menu = (
    <WorkspaceMenu
      accounts={list}
      selectedAccountId={selectedAccountId}
      isPending={isPending}
      showEmpty={showEmpty}
      canAddWorkspace={canAddWorkspace}
      onSelect={closeAndSelect}
      onAdd={closeAndAdd}
    />
  );

  const hoverCard = (
    trigger: React.ReactNode,
    side: 'right' | 'bottom',
    wrapClassName = 'w-full min-w-0',
    triggerClassName = 'w-full',
  ) => (
    <div className={wrapClassName}>
      <HoverCard
        open={open}
        onOpenChange={setOpen}
        openDelay={80}
        closeDelay={220}
      >
        <HoverCardTrigger asChild className={triggerClassName}>{trigger}</HoverCardTrigger>
        <HoverCardContent
          side={side}
          align="start"
          sideOffset={8}
          collisionPadding={
            side === 'right'
              ? { top: 72, right: 8, bottom: 8, left: 8 }
              : 8
          }
          className="w-[280px] rounded-2xl border border-[#EAECF0] bg-white p-2 text-[14px] shadow-[0_12px_32px_rgba(16,24,40,0.12)] z-[80]"
        >
          {menu}
        </HoverCardContent>
      </HoverCard>
    </div>
  );

  if (collapsed) {
    return (
      <div
        className="flex w-full min-w-0 justify-center"
        data-testid="sidebar-workspace-switcher"
      >
        {hoverCard(
          <button
            type="button"
            data-tour="business-account-selector"
            aria-label={selectedAccount?.name ? `Workspace: ${selectedAccount.name}` : 'Workspaces'}
            aria-expanded={open}
            onClick={() => setOpen(true)}
            className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full outline-none hover:opacity-90"
          >
            <WorkspaceAvatar
              accountId={selectedAccount?.id}
              name={selectedAccount?.name}
              sizeClass="h-8 w-8"
              loading="eager"
            />
          </button>,
          'right',
          'flex w-full min-w-0 justify-center',
          'inline-flex shrink-0',
        )}
      </div>
    );
  }

  const triggerLabel = isPending
    ? 'Loading…'
    : selectedAccount?.name || (showEmpty ? 'No workspace' : 'Select workspace');

  return (
    <div
      data-testid="sidebar-workspace-switcher"
      className="shrink-0 -mx-5 px-3"
    >
      {hoverCard(
        <button
          type="button"
          data-tour="business-account-selector"
          aria-label={selectedAccount?.name ? `Workspace: ${selectedAccount.name}` : 'Workspaces'}
          aria-expanded={open}
          onClick={() => setOpen(true)}
          className="flex w-full min-w-0 items-center gap-2 rounded-lg border border-[#EAECF0] bg-white px-2 py-1.5 text-left outline-none hover:border-[#D0D5DD] focus-visible:ring-2 focus-visible:ring-[#12B76A]/30"
        >
          {isPending ? (
            <div className="h-7 w-7 shrink-0 rounded-md bg-[#F2F4F7]" aria-hidden />
          ) : (
            <WorkspaceAvatar
              accountId={selectedAccount?.id}
              name={selectedAccount?.name}
              sizeClass="h-7 w-7"
              loading="eager"
            />
          )}
          <div className="min-w-0 flex-1 truncate text-[14px] font-medium text-[#101828]">
            {triggerLabel}
          </div>
          <Repeat
            aria-hidden
            className="ml-auto h-4 w-4 shrink-0 text-[#98A2B3]"
          />
        </button>,
        'bottom',
      )}
    </div>
  );
}
